import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '~/app/auth/context/AuthContext';
import { PreventiveMaintenanceService } from '~/app/services/preventive-maintenance/preventive-maintenance.service';
import type { StepAssetsFilterState } from '../types';
import { toSelectOptions } from '../utils';

/**
 * Filter options for wizard Step 3 from dedicated thin catalog APIs
 * (full tenant/global catalogs — not derived from assignable assets).
 * Fetches only when `enabled` (caller should pass `!skipAssets`).
 */
export function useStepAssetsFilterOptions(
  filters: StepAssetsFilterState,
  options: { enabled?: boolean } = {},
) {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const enabled = options.enabled ?? true;
  const tenantCatalogEnabled = enabled && !!organizationId;

  const landlordsQuery = useQuery({
    queryKey: [
      'preventive-maintenance',
      'wizard',
      'filter-landlords',
      organizationId,
    ],
    queryFn: () => PreventiveMaintenanceService.listWizardFilterLandlords(),
    enabled: tenantCatalogEnabled,
  });

  const assetTypesQuery = useQuery({
    queryKey: ['preventive-maintenance', 'wizard', 'filter-asset-types'],
    queryFn: () => PreventiveMaintenanceService.listWizardFilterAssetTypes(),
    enabled,
  });

  const assetModelsQuery = useQuery({
    queryKey: [
      'preventive-maintenance',
      'wizard',
      'filter-asset-models',
      [...filters.assetTypeIds].sort().join(','),
    ],
    queryFn: () =>
      PreventiveMaintenanceService.listWizardFilterAssetModels(
        filters.assetTypeIds,
      ),
    enabled,
  });

  const sitesQuery = useQuery({
    queryKey: [
      'preventive-maintenance',
      'wizard',
      'filter-sites',
      organizationId,
    ],
    queryFn: () => PreventiveMaintenanceService.listWizardFilterSites(),
    enabled: tenantCatalogEnabled,
  });

  const landlordOptions = useMemo(
    () => toSelectOptions(landlordsQuery.data?.data ?? []),
    [landlordsQuery.data],
  );
  const assetTypeOptions = useMemo(
    () => toSelectOptions(assetTypesQuery.data?.data ?? []),
    [assetTypesQuery.data],
  );
  const assetModelOptions = useMemo(
    () => toSelectOptions(assetModelsQuery.data?.data ?? []),
    [assetModelsQuery.data],
  );
  const siteOptions = useMemo(
    () => toSelectOptions(sitesQuery.data?.data ?? []),
    [sitesQuery.data],
  );

  return {
    assetTypeOptions,
    assetModelOptions,
    landlordOptions,
    siteOptions,
    isLoadingFacets:
      assetTypesQuery.isLoading ||
      assetModelsQuery.isLoading ||
      sitesQuery.isLoading,
    isLoadingLandlords: landlordsQuery.isLoading,
  };
}
